/**
 * exam-archive — 拍照上傳批改後,把**整張卷**(沒丟棄的題,含答對的)歸檔進
 * `progress.uploadedExams`,讓考卷成為永久資產。
 *
 * 緣由(boss 2026-06-10「考卷沒錯的地方就沒了」):原流程批改完,錯題進錯題本、
 * 對題只默默升熟練度,題目本身蒸發;「出變形練習卷」也只能在完成畫面當下按一次。
 * 歸檔後:考卷紀錄畫面隨時可重出變形卷、查看原卷(含重繪圖表)。
 *
 * 鐵則:純學習側資料,絕不碰 commanderXp / 戰鬥數值。
 */

import type { ArchivedExam, ArchivedExamItem, PlayerProgress, Subject } from '../../shared/types';
import type { PromotionInput } from './promote';
import type { ExtractedItem } from './ocr-client';
import { normalizeFigure } from './figure';

/** 歸檔考卷張數上限(FIFO,最舊的先掉)。20 張 × ~15 題對 localStorage 很輕。 */
export const MAX_ARCHIVED_EXAMS = 20;

/**
 * 把一次家長確認的結果歸檔成一張卷。discard 的題不收;整卷都 discard 則不建檔
 * (回 null)。新卷插在最前面,超過上限砍尾。
 */
export function archiveExam(
  progress: PlayerProgress,
  inputs: PromotionInput[],
  nowISO: string = new Date().toISOString(),
): ArchivedExam | null {
  const kept = inputs.filter((i) => i.decision !== 'discard');
  if (kept.length === 0) return null;

  if (!Array.isArray(progress.uploadedExams)) progress.uploadedExams = [];

  const items: ArchivedExamItem[] = kept.map(({ item, decision }) => {
    const out: ArchivedExamItem = {
      subject: item.subject as Subject,
      kp: item.kp,
      kind: item.kind,
      prompt: item.prompt,
      correctAnswer: item.correctAnswer,
      decision: decision as ArchivedExamItem['decision'],
    };
    if (item.options && item.options.length > 0) out.options = item.options;
    if (item.studentAnswer && item.studentAnswer.trim() !== '') out.studentAnswer = item.studentAnswer;
    if (item.explanation && item.explanation.trim() !== '') out.explanation = item.explanation.trim();
    if (item.figure) out.figure = item.figure;
    return out;
  });

  const exam: ArchivedExam = {
    id: `exam-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    dateISO: nowISO,
    items,
  };
  progress.uploadedExams.unshift(exam);
  if (progress.uploadedExams.length > MAX_ARCHIVED_EXAMS) {
    progress.uploadedExams.length = MAX_ARCHIVED_EXAMS;
  }
  return exam;
}

/**
 * 歸檔題 → ExtractedItem + decision(供 runVariantPractice 重點挑題 /
 * renderFigure 重用)。figure 是存進 localStorage 的原樣 JSON,這裡重新
 * normalizeFigure 驗一次,壞圖直接丟掉(題目仍保留)。
 */
export function archivedToExtracted(
  items: ArchivedExamItem[],
): Array<ExtractedItem & { decision: ArchivedExamItem['decision'] }> {
  return items.map((it) => {
    const out: ExtractedItem & { decision: ArchivedExamItem['decision'] } = {
      decision: it.decision,
      subject: it.subject,
      kp: it.kp,
      kind: it.kind,
      prompt: it.prompt,
      correctAnswer: it.correctAnswer,
      explanation: it.explanation ?? '',
    };
    if (it.options) out.options = it.options;
    if (it.studentAnswer) out.studentAnswer = it.studentAnswer;
    const fig = normalizeFigure(it.figure);
    if (fig) out.figure = fig;
    return out;
  });
}

/** 一張卷的對/錯統計(粗心算錯的一種,因為當下沒拿到分)。 */
export function examStats(exam: ArchivedExam): { total: number; correct: number; wrong: number } {
  const total = exam.items.length;
  const correct = exam.items.filter((i) => i.decision === 'correct').length;
  return { total, correct, wrong: total - correct };
}
